import { v3ApiHttp } from '@/services/api/tattle-http'
import type {
  AdminPermission,
  PermissionMetadata,
  AvailablePermissionsResponse,
  AdministratorPermissionsResponse,
  CreateAdministratorPermissionsRequest,
  UpdateAdministratorPermissionsRequest,
  GrantPermissionRequest,
  SetSuperAdminRequest
} from './administrator-permissions.types'

// Re-export types for convenience
export { AdminPermission } from './administrator-permissions.types'
export type {
  PermissionMetadata,
  AvailablePermissionsResponse,
  AdministratorPermissionsResponse,
  CreateAdministratorPermissionsRequest,
  UpdateAdministratorPermissionsRequest,
  GrantPermissionRequest,
  SetSuperAdminRequest
} from './administrator-permissions.types'

export const administratorPermissionsAPI = {
  /**
   * Get all available admin permissions with metadata
   */
  async getAvailablePermissions() {
    const response = await v3ApiHttp.get<AvailablePermissionsResponse>(
      '/admin/administrators/permissions/available'
    )
    return response
  },

  /**
   * Get permissions for a specific administrator
   */
  async getPermissions(administratorId: number) {
    const response = await v3ApiHttp.get<AdministratorPermissionsResponse>(
      `/admin/administrators/${administratorId}/permissions`
    )
    return response
  },

  /**
   * Create permissions for a new administrator
   */
  async createPermissions(administratorId: number, data: CreateAdministratorPermissionsRequest) {
    const response = await v3ApiHttp.post<AdministratorPermissionsResponse>(
      `/admin/administrators/${administratorId}/permissions`,
      data
    )
    return response
  },

  /**
   * Update permissions for an existing administrator
   */
  async updatePermissions(administratorId: number, data: UpdateAdministratorPermissionsRequest) {
    const response = await v3ApiHttp.put<AdministratorPermissionsResponse>(
      `/admin/administrators/${administratorId}/permissions`,
      data
    )
    return response
  },

  /**
   * Grant or revoke a single permission for an administrator
   */
  async grantOrRevokePermission(administratorId: number, data: GrantPermissionRequest) {
    const response = await v3ApiHttp.post<void>(
      `/admin/administrators/${administratorId}/permissions/grant`,
      data
    )
    return response
  },

  /**
   * Set or remove super admin status for an administrator
   */
  async setSuperAdmin(administratorId: number, data: SetSuperAdminRequest) {
    const response = await v3ApiHttp.post<void>(
      `/admin/administrators/${administratorId}/super-admin`,
      data
    )
    return response
  },

  /**
   * Delete permissions for an administrator (reverts to no permissions)
   */
  async deletePermissions(administratorId: number) {
    const response = await v3ApiHttp.delete<void>(
      `/admin/administrators/${administratorId}/permissions`
    )
    return response
  },

  /**
   * Get permissions for the current logged-in administrator
   */
  async getMyPermissions() {
    const response = await v3ApiHttp.get<AdministratorPermissionsResponse>(
      '/admin/administrators/me/permissions'
    )
    return response
  }
}
